// API Service for HyperdriveTrade
const API_URL = process.env.REACT_APP_BACKEND_URL;

// Export API_URL for components that need direct access
export const API = API_URL;

// Helper to get auth header
const getAuthHeader = () => {
  const token = localStorage.getItem('hyperdriveToken');
  return token ? { 'Authorization': `Bearer ${token}` } : {};
};

// Auth API
export const authApi = {
  register: async (name, email, password) => {
    const response = await fetch(`${API_URL}/api/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name, email, password })
    });
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.detail || 'Registration failed. Please try again.');
    }
    return data;
  },

  login: async (email, password, rememberMe = false) => {
    try {
      const response = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password, remember_me: rememberMe })
      });
      if (!response.ok) {
        // Try to get the actual error message from the backend
        try {
          const errorData = await response.json();
          const detail = errorData.detail || 'Invalid credentials';
          // Check for specific error messages
          if (detail.includes('password reset') || detail.includes('contact support')) {
            throw new Error(detail);
          }
        } catch (parseError) {
          // Response body not JSON; fall through to generic message
          console.warn('Login error response could not be parsed as JSON:', parseError);
        }
        throw new Error('Email or password entered incorrectly. Please check your information and try again.');
      }
      return await response.json();
    } catch (err) {
      // Re-throw if it's already a proper error message
      if (err.message && !err.message.includes('fetch')) {
        throw err;
      }
      throw new Error('Unable to connect to server. Please try again.');
    }
  },

  getMe: async () => {
    const token = localStorage.getItem('hyperdriveToken');
    console.log('getMe called, token exists:', !!token);
    
    if (!token) {
      throw new Error('No token in localStorage');
    }
    
    const response = await fetch(`${API_URL}/api/auth/me`, {
      headers: { ...getAuthHeader() }
    });
    
    console.log('getMe response status:', response.status);
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('getMe failed:', response.status, errorText);
      throw new Error('Not authenticated');
    }
    return response.json();
  },

  updateMe: async (data) => {
    const response = await fetch(`${API_URL}/api/auth/me`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(data)
    });
    if (!response.ok) throw new Error('Update failed');
    return response.json();
  },

  completeOnboarding: async () => {
    const response = await fetch(`${API_URL}/api/auth/complete-onboarding`, {
      method: 'POST',
      headers: { ...getAuthHeader() }
    });
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.detail || 'Failed to complete onboarding');
    }
    return data;
  }
};

// Upload API
export const uploadApi = {
  uploadImage: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await fetch(`${API_URL}/api/upload/image`, {
      method: 'POST',
      headers: { ...getAuthHeader() },
      body: formData
    });
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.detail || 'Upload failed');
    }
    return data;
  },

  uploadVideo: async (file, onProgress) => {
    const LARGE_FILE_THRESHOLD = 25 * 1024 * 1024; // 25MB — anything larger uses chunked upload
    const token = localStorage.getItem('hyperdriveToken');

    // Large files: chunked upload to Emergent Object Storage via backend
    if (file.size > LARGE_FILE_THRESHOLD) {
      console.log(`[Upload] Large file (${(file.size / 1024 / 1024).toFixed(2)}MB) — using chunked upload`);
      const CHUNK_SIZE = 5 * 1024 * 1024; // 5MB per chunk — well under any proxy body limit

      // Step 1: init session
      const initRes = await fetch(
        `${API_URL}/api/upload/video/chunk/init?filename=${encodeURIComponent(file.name)}&content_type=${encodeURIComponent(file.type)}&total_size=${file.size}`,
        { method: 'POST', headers: { Authorization: `Bearer ${token}` } }
      );
      if (!initRes.ok) {
        const err = await initRes.json().catch(() => ({}));
        throw new Error(err.detail || `Failed to start upload (HTTP ${initRes.status})`);
      }
      const { upload_id } = await initRes.json();

      // Step 2: upload chunks sequentially
      const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
      for (let i = 0; i < totalChunks; i++) {
        const start = i * CHUNK_SIZE;
        const end = Math.min(start + CHUNK_SIZE, file.size);
        const chunk = file.slice(start, end);
        const form = new FormData();
        form.append('chunk', chunk, `chunk_${i}`);

        const chunkRes = await fetch(
          `${API_URL}/api/upload/video/chunk/upload?upload_id=${encodeURIComponent(upload_id)}&chunk_index=${i}`,
          { method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: form }
        );
        if (!chunkRes.ok) {
          const err = await chunkRes.json().catch(() => ({}));
          throw new Error(err.detail || `Chunk ${i + 1}/${totalChunks} failed (HTTP ${chunkRes.status})`);
        }
        if (onProgress) {
          onProgress(Math.round(((i + 1) / totalChunks) * 100));
        }
      }

      // Step 3: complete — backend kicks off async assembly + upload to Emergent Storage
      const completeRes = await fetch(
        `${API_URL}/api/upload/video/chunk/complete?upload_id=${encodeURIComponent(upload_id)}`,
        { method: 'POST', headers: { Authorization: `Bearer ${token}` } }
      );
      if (!completeRes.ok) {
        const err = await completeRes.json().catch(() => ({}));
        throw new Error(err.detail || `Failed to finalize upload (HTTP ${completeRes.status})`);
      }
      const completeData = await completeRes.json();

      // If backend went async (large file), poll status until ready.
      // Small files may already be 'ready' on first response — handle both.
      if (completeData.status && completeData.status !== 'ready') {
        const fileId = completeData.id;
        const POLL_INTERVAL_MS = 2500;
        const MAX_POLL_MS = 15 * 60 * 1000; // 15 minutes for very large uploads
        const startedAt = Date.now();
        // Poll until finalization completes
        while (true) {
          if (Date.now() - startedAt > MAX_POLL_MS) {
            throw new Error('Upload finalization timed out. Please try again.');
          }
          await new Promise(r => setTimeout(r, POLL_INTERVAL_MS));
          const statusRes = await fetch(
            `${API_URL}/api/upload/video/chunk/status?file_id=${encodeURIComponent(fileId)}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          if (!statusRes.ok) {
            // Transient network/edge errors during poll — keep trying until MAX_POLL_MS
            continue;
          }
          const statusData = await statusRes.json();
          if (statusData.status === 'ready') {
            return statusData;
          }
          if (statusData.status === 'failed') {
            throw new Error(statusData.error || 'Upload finalization failed on server');
          }
          // else 'processing' — keep polling
        }
      }
      return completeData;
    }

    // Small files: single POST
    return new Promise((resolve, reject) => {
      const formData = new FormData();
      formData.append('file', file);
      
      const xhr = new XMLHttpRequest();
      xhr.open('POST', `${API_URL}/api/upload/video`);
      
      if (token) {
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      }
      
      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable && onProgress) {
          const percent = Math.round((event.loaded / event.total) * 100);
          onProgress(percent);
        }
      };
      
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            const data = JSON.parse(xhr.responseText);
            resolve(data);
          } catch (e) {
            reject(new Error('Invalid response'));
          }
        } else {
          try {
            const data = JSON.parse(xhr.responseText);
            reject(new Error(data.detail || 'Upload failed'));
          } catch (e) {
            reject(new Error('Upload failed'));
          }
        }
      };
      
      xhr.onerror = () => reject(new Error('Network error'));
      xhr.ontimeout = () => reject(new Error('Upload timeout'));
      xhr.timeout = 0;
      
      xhr.send(formData);
    });
  },

  uploadFile: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await fetch(`${API_URL}/api/upload/file`, {
      method: 'POST',
      headers: { ...getAuthHeader() },
      body: formData
    });
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.detail || 'Upload failed');
    }
    return data;
  },

  getFileUrl: (path) => `${API_URL}${path}`,
  
  // Resolve profile picture URL - handles both relative paths and legacy absolute URLs
  getProfilePictureUrl: (profilePicture, cacheBuster = null) => {
    if (!profilePicture) return null;
    let url;
    // If it's already an absolute URL (legacy data), use it as-is
    if (profilePicture.startsWith('http://') || profilePicture.startsWith('https://')) {
      // If the domain doesn't match current API_URL, try to extract and rebuild the path
      if (!profilePicture.startsWith(API_URL)) {
        // Extract the relative path from the old URL
        const match = profilePicture.match(/\/api\/upload\/.+$/);
        if (match) {
          url = `${API_URL}${match[0]}`;
        } else {
          url = profilePicture;
        }
      } else {
        url = profilePicture;
      }
    } else {
      // If it's a relative path, prepend API_URL
      url = `${API_URL}${profilePicture}`;
    }
    // Add cache buster if provided to force browser to reload the image
    if (cacheBuster) {
      url += (url.includes('?') ? '&' : '?') + `v=${cacheBuster}`;
    }
    return url;
  }
};

// Trades API
export const tradesApi = {
  getAll: async () => {
    const response = await fetch(`${API_URL}/api/trades`, {
      headers: { ...getAuthHeader() }
    });
    if (!response.ok) throw new Error('Failed to fetch trades');
    return response.json();
  },

  create: async (tradeData) => {
    const response = await fetch(`${API_URL}/api/trades`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(tradeData)
    });
    if (!response.ok) throw new Error('Failed to create trade');
    return response.json();
  },

  update: async (tradeId, tradeData) => {
    const response = await fetch(`${API_URL}/api/trades/${tradeId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(tradeData)
    });
    if (!response.ok) throw new Error('Failed to update trade');
    return response.json();
  },

  delete: async (tradeId) => {
    const response = await fetch(`${API_URL}/api/trades/${tradeId}`, {
      method: 'DELETE',
      headers: { ...getAuthHeader() }
    });
    if (!response.ok) throw new Error('Failed to delete trade');
    return response.json();
  },

  // Custom Pairs API
  getCustomPairs: async () => {
    const response = await fetch(`${API_URL}/api/trades/custom-pairs`, {
      headers: { ...getAuthHeader() }
    });
    if (!response.ok) throw new Error('Failed to fetch custom pairs');
    return response.json();
  },

  addCustomPair: async (pair) => {
    const response = await fetch(`${API_URL}/api/trades/custom-pairs`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify({ pair })
    });
    if (!response.ok) throw new Error('Failed to add custom pair');
    return response.json();
  },

  deleteCustomPair: async (pair) => {
    const response = await fetch(`${API_URL}/api/trades/custom-pairs/${encodeURIComponent(pair)}`, {
      method: 'DELETE',
      headers: { ...getAuthHeader() }
    });
    if (!response.ok) throw new Error('Failed to delete custom pair');
    return response.json();
  }
};

// Trading Plan API
export const tradingPlanApi = {
  // Rules CRUD
  getRules: async () => {
    const response = await fetch(`${API_URL}/api/trading-plan/rules`, {
      headers: { ...getAuthHeader() }
    });
    if (!response.ok) throw new Error('Failed to fetch trading plan rules');
    return response.json();
  },

  createRule: async (ruleText, order = 0) => {
    const response = await fetch(`${API_URL}/api/trading-plan/rules`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify({ rule_text: ruleText, order })
    });
    if (!response.ok) throw new Error('Failed to create rule');
    return response.json();
  },

  updateRule: async (ruleId, updates) => {
    const response = await fetch(`${API_URL}/api/trading-plan/rules/${ruleId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(updates)
    });
    if (!response.ok) throw new Error('Failed to update rule');
    return response.json();
  },

  deleteRule: async (ruleId) => {
    const response = await fetch(`${API_URL}/api/trading-plan/rules/${ruleId}`, {
      method: 'DELETE',
      headers: { ...getAuthHeader() }
    });
    if (!response.ok) throw new Error('Failed to delete rule');
    return response.json();
  },

  reorderRules: async (ruleOrders) => {
    const response = await fetch(`${API_URL}/api/trading-plan/rules/reorder`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify({ rules: ruleOrders })
    });
    if (!response.ok) throw new Error('Failed to reorder rules');
    return response.json();
  },

  // Daily Habit Tracking
  getDailyHabitLog: async (date) => {
    const response = await fetch(`${API_URL}/api/trading-plan/habits/${date}`, {
      headers: { ...getAuthHeader() }
    });
    if (!response.ok) throw new Error('Failed to fetch habit log');
    return response.json();
  },

  getMonthlyHabitLogs: async (year, month) => {
    const response = await fetch(`${API_URL}/api/trading-plan/habits?year=${year}&month=${month}`, {
      headers: { ...getAuthHeader() }
    });
    if (!response.ok) throw new Error('Failed to fetch monthly habit logs');
    return response.json();
  },

  saveHabitLog: async (date, entries) => {
    const response = await fetch(`${API_URL}/api/trading-plan/habits`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify({ date, entries })
    });
    if (!response.ok) throw new Error('Failed to save habit log');
    return response.json();
  },

  getMonthlyStats: async (year, month) => {
    const response = await fetch(`${API_URL}/api/trading-plan/habits/stats/monthly?year=${year}&month=${month}`, {
      headers: { ...getAuthHeader() }
    });
    if (!response.ok) throw new Error('Failed to fetch monthly stats');
    return response.json();
  },

  getHabitTrends: async (days = 90) => {
    const response = await fetch(`${API_URL}/api/trading-plan/habit-trends?days=${days}`, {
      headers: { ...getAuthHeader() }
    });
    if (!response.ok) throw new Error('Failed to fetch habit trends');
    return response.json();
  }
};

// In-app notifications / habit nudges
export const notificationsApi = {
  list: async () => {
    const response = await fetch(`${API_URL}/api/notifications`, {
      headers: { ...getAuthHeader() }
    });
    if (!response.ok) throw new Error('Failed to fetch notifications');
    return response.json();
  },
  dismiss: async (notificationId) => {
    const response = await fetch(`${API_URL}/api/notifications/${notificationId}/dismiss`, {
      method: 'POST',
      headers: { ...getAuthHeader() }
    });
    if (!response.ok) throw new Error('Failed to dismiss notification');
    return response.json();
  },
  generateMine: async () => {
    const response = await fetch(`${API_URL}/api/notifications/generate-mine`, {
      method: 'POST',
      headers: { ...getAuthHeader() }
    });
    if (!response.ok) throw new Error('Failed to generate nudges');
    return response.json();
  },
};

// Courses API - Kajabi-style course builder
export const coursesApi = {
  getAll: async () => {
    const response = await fetch(`${API_URL}/api/courses`, {
      headers: { ...getAuthHeader() }
    });
    if (!response.ok) throw new Error('Failed to fetch courses');
    return response.json();
  },

  getAllAdmin: async () => {
    const response = await fetch(`${API_URL}/api/courses/all/admin`, {
      headers: { ...getAuthHeader() }
    });
    if (!response.ok) throw new Error('Failed to fetch courses');
    return response.json();
  },

  getFeatured: async () => {
    const response = await fetch(`${API_URL}/api/courses/featured/homepage`);
    if (!response.ok) throw new Error('Failed to fetch featured courses');
    return response.json();
  },

  get: async (courseId) => {
    const response = await fetch(`${API_URL}/api/courses/${courseId}`);
    if (!response.ok) throw new Error('Failed to fetch course');
    return response.json();
  },

  create: async (courseData) => {
    const response = await fetch(`${API_URL}/api/courses`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(courseData)
    });
    if (!response.ok) throw new Error('Failed to create course');
    return response.json();
  },

  update: async (courseId, courseData) => {
    const response = await fetch(`${API_URL}/api/courses/${courseId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(courseData)
    });
    if (!response.ok) throw new Error('Failed to update course');
    return response.json();
  },

  delete: async (courseId) => {
    const response = await fetch(`${API_URL}/api/courses/${courseId}`, {
      method: 'DELETE',
      headers: { ...getAuthHeader() }
    });
    if (!response.ok) throw new Error('Failed to delete course');
    return response.json();
  },

  toggleArchive: async (courseId) => {
    const response = await fetch(`${API_URL}/api/courses/${courseId}/archive`, {
      method: 'PUT',
      headers: { ...getAuthHeader() }
    });
    if (!response.ok) throw new Error('Failed to toggle archive status');
    return response.json();
  },

  // Section methods
  addSection: async (courseId, section) => {
    const response = await fetch(`${API_URL}/api/courses/${courseId}/sections`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(section)
    });
    if (!response.ok) throw new Error('Failed to add section');
    return response.json();
  },

  updateSection: async (courseId, sectionId, section) => {
    const response = await fetch(`${API_URL}/api/courses/${courseId}/sections/${sectionId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(section)
    });
    if (!response.ok) throw new Error('Failed to update section');
    return response.json();
  },

  deleteSection: async (courseId, sectionId) => {
    const response = await fetch(`${API_URL}/api/courses/${courseId}/sections/${sectionId}`, {
      method: 'DELETE',
      headers: { ...getAuthHeader() }
    });
    if (!response.ok) throw new Error('Failed to delete section');
    return response.json();
  },

  reorderSections: async (courseId, sectionIds) => {
    const response = await fetch(`${API_URL}/api/courses/${courseId}/reorder-sections`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(sectionIds)
    });
    if (!response.ok) throw new Error('Failed to reorder sections');
    return response.json();
  },

  // Episode methods
  addEpisode: async (courseId, sectionId, episode) => {
    const response = await fetch(`${API_URL}/api/courses/${courseId}/sections/${sectionId}/episodes`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(episode)
    });
    if (!response.ok) throw new Error('Failed to add episode');
    return response.json();
  },

  updateEpisode: async (courseId, sectionId, episodeId, episode) => {
    const response = await fetch(`${API_URL}/api/courses/${courseId}/sections/${sectionId}/episodes/${episodeId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(episode)
    });
    if (!response.ok) throw new Error('Failed to update episode');
    return response.json();
  },

  deleteEpisode: async (courseId, sectionId, episodeId) => {
    const response = await fetch(`${API_URL}/api/courses/${courseId}/sections/${sectionId}/episodes/${episodeId}`, {
      method: 'DELETE',
      headers: { ...getAuthHeader() }
    });
    if (!response.ok) throw new Error('Failed to delete episode');
    return response.json();
  },

  reorderEpisodes: async (courseId, sectionId, episodeIds) => {
    const response = await fetch(`${API_URL}/api/courses/${courseId}/sections/${sectionId}/reorder-episodes`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(episodeIds)
    });
    if (!response.ok) throw new Error('Failed to reorder episodes');
    return response.json();
  },

  // Video Progress methods
  saveProgress: async (episodeId, currentTime, duration) => {
    const response = await fetch(`${API_URL}/api/courses/progress`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify({ episode_id: episodeId, current_time: currentTime, duration: duration })
    });
    if (!response.ok) throw new Error('Failed to save progress');
    return response.json();
  },

  getProgress: async (episodeId) => {
    const response = await fetch(`${API_URL}/api/courses/progress/${episodeId}`, {
      headers: { ...getAuthHeader() }
    });
    if (!response.ok) throw new Error('Failed to get progress');
    return response.json();
  },

  getAllProgress: async () => {
    const response = await fetch(`${API_URL}/api/courses/progress/all`, {
      headers: { ...getAuthHeader() }
    });
    if (!response.ok) throw new Error('Failed to get progress');
    return response.json();
  },

  clearProgress: async (episodeId) => {
    const response = await fetch(`${API_URL}/api/courses/progress/${episodeId}`, {
      method: 'DELETE',
      headers: { ...getAuthHeader() }
    });
    if (!response.ok) throw new Error('Failed to clear progress');
    return response.json();
  },

  // Episode completion tracking
  markComplete: async (courseId, episodeId, completed = true) => {
    const response = await fetch(`${API_URL}/api/courses/completions`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify({ course_id: courseId, episode_id: episodeId, completed })
    });
    if (!response.ok) throw new Error('Failed to mark episode');
    return response.json();
  },

  getCompletions: async (courseId) => {
    const response = await fetch(`${API_URL}/api/courses/completions/${courseId}`, {
      headers: { ...getAuthHeader() }
    });
    if (!response.ok) throw new Error('Failed to get completions');
    return response.json();
  }
};

// Enrollment API
export const enrollmentApi = {
  getMyEnrollments: async () => {
    const response = await fetch(`${API_URL}/api/enrollment/my-enrollments`, {
      headers: { ...getAuthHeader() }
    });
    if (!response.ok) throw new Error('Failed to fetch enrollments');
    return response.json();
  },

  checkEnrollment: async (courseId) => {
    const response = await fetch(`${API_URL}/api/enrollment/check/${courseId}`, {
      headers: { ...getAuthHeader() }
    });
    if (!response.ok) throw new Error('Failed to check enrollment');
    return response.json();
  },

  enrollInCourse: async (courseId, couponCode = null) => {
    const originUrl = window.location.origin;
    const body = { course_id: courseId, origin_url: originUrl };
    if (couponCode) {
      body.coupon_code = couponCode;
    }
    const response = await fetch(`${API_URL}/api/enrollment/enroll`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(body)
    });
    if (!response.ok) {
      let errorMessage = 'Failed to enroll';
      try {
        const errorData = await response.json();
        errorMessage = errorData.detail || errorMessage;
      } catch (e) {
        // Response wasn't JSON
        errorMessage = `Failed to enroll (${response.status})`;
      }
      throw new Error(errorMessage);
    }
    return response.json();
  },

  checkPaymentStatus: async (sessionId) => {
    const response = await fetch(`${API_URL}/api/enrollment/payment-status/${sessionId}`, {
      headers: { ...getAuthHeader() }
    });
    if (!response.ok) throw new Error('Failed to check payment status');
    return response.json();
  }
};

// Membership Payment API
export const membershipApi = {
  upgradeMembership: async (membershipId, couponCode = null) => {
    const body = { 
      membership_id: membershipId,
      origin_url: window.location.origin
    };
    if (couponCode) {
      body.coupon_code = couponCode;
    }
    const response = await fetch(`${API_URL}/api/membership/upgrade`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(body)
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || 'Failed to initiate membership upgrade');
    }
    return response.json();
  },

  checkPaymentStatus: async (sessionId) => {
    const response = await fetch(`${API_URL}/api/membership/payment-status/${sessionId}`, {
      headers: { ...getAuthHeader() }
    });
    if (!response.ok) throw new Error('Failed to check payment status');
    return response.json();
  },

  openBillingPortal: async () => {
    const response = await fetch(`${API_URL}/api/membership/billing-portal`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify({ 
        return_url: window.location.href
      })
    });
    if (!response.ok) throw new Error('Failed to open billing portal');
    return response.json();
  }
};

// Community API
export const communityApi = {
  getChannels: async () => {
    const response = await fetch(`${API_URL}/api/community/channels`);
    if (!response.ok) throw new Error('Failed to fetch channels');
    return response.json();
  },

  createChannel: async (channelData) => {
    const response = await fetch(`${API_URL}/api/community/channels`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(channelData)
    });
    if (!response.ok) throw new Error('Failed to create channel');
    return response.json();
  },

  updateChannel: async (channelId, channelData) => {
    const response = await fetch(`${API_URL}/api/community/channels/${channelId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(channelData)
    });
    if (!response.ok) throw new Error('Failed to update channel');
    return response.json();
  },

  deleteChannel: async (channelId) => {
    const response = await fetch(`${API_URL}/api/community/channels/${channelId}`, {
      method: 'DELETE',
      headers: { ...getAuthHeader() }
    });
    if (!response.ok) throw new Error('Failed to delete channel');
    return response.json();
  },

  getMessages: async (channel) => {
    const response = await fetch(`${API_URL}/api/community/messages/${channel}`);
    if (!response.ok) throw new Error('Failed to fetch messages');
    return response.json();
  },

  sendMessage: async (channel, text) => {
    const response = await fetch(`${API_URL}/api/community/messages`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify({ channel, text })
    });
    if (!response.ok) throw new Error('Failed to send message');
    return response.json();
  },

  getMemberships: async () => {
    const response = await fetch(`${API_URL}/api/community/memberships`);
    if (!response.ok) throw new Error('Failed to fetch memberships');
    return response.json();
  },

  getAllMemberships: async () => {
    const response = await fetch(`${API_URL}/api/community/memberships/all`, {
      headers: { ...getAuthHeader() }
    });
    if (!response.ok) throw new Error('Failed to fetch all memberships');
    return response.json();
  },

  createMembership: async (membershipData) => {
    const response = await fetch(`${API_URL}/api/community/memberships`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(membershipData)
    });
    if (!response.ok) throw new Error('Failed to create membership');
    return response.json();
  },

  updateMembership: async (membershipId, membershipData) => {
    const response = await fetch(`${API_URL}/api/community/memberships/${membershipId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(membershipData)
    });
    if (!response.ok) throw new Error('Failed to update membership');
    return response.json();
  },

  deleteMembership: async (membershipId) => {
    const response = await fetch(`${API_URL}/api/community/memberships/${membershipId}`, {
      method: 'DELETE',
      headers: { ...getAuthHeader() }
    });
    if (!response.ok) throw new Error('Failed to delete membership');
    return response.json();
  },

  // Presence system - platform-wide online status
  sendHeartbeat: async () => {
    const response = await fetch(`${API_URL}/api/community/presence/heartbeat`, {
      method: 'POST',
      headers: { ...getAuthHeader() }
    });
    if (!response.ok) throw new Error('Failed to send heartbeat');
    return response.json();
  },

  getOnlineUsers: async () => {
    const response = await fetch(`${API_URL}/api/community/presence/online`, {
      headers: { ...getAuthHeader() }
    });
    if (!response.ok) throw new Error('Failed to get online users');
    return response.json();
  },

  // Unread messages system
  markChannelRead: async (channel) => {
    const response = await fetch(`${API_URL}/api/community/mark-read/${channel}`, {
      method: 'POST',
      headers: { ...getAuthHeader() }
    });
    if (!response.ok) throw new Error('Failed to mark channel read');
    return response.json();
  },

  getUnreadCounts: async () => {
    try {
      const response = await fetch(`${API_URL}/api/community/unread-counts`, {
        headers: { ...getAuthHeader() }
      });
      if (!response.ok) {
        console.error('Unread counts API error:', response.status);
        throw new Error('Failed to get unread counts');
      }
      const data = await response.json();
      console.log('Unread counts response:', data);
      return data;
    } catch (err) {
      console.error('Unread counts fetch error:', err);
      throw err;
    }
  }
};

// Stream API
export const streamApi = {
  getStatus: async () => {
    const response = await fetch(`${API_URL}/api/stream`);
    if (!response.ok) throw new Error('Failed to fetch stream status');
    return response.json();
  },

  update: async (streamData) => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 15000); // 15 second timeout
    
    try {
      const response = await fetch(`${API_URL}/api/stream`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
        body: JSON.stringify(streamData),
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Stream update failed:', response.status, errorText);
        throw new Error(`Failed to update stream: ${response.status}`);
      }
      
      return response.json();
    } catch (error) {
      clearTimeout(timeoutId);
      if (error.name === 'AbortError') {
        throw new Error('Request timed out. Please try again.');
      }
      throw error;
    }
  },

  toggleLive: async () => {
    const response = await fetch(`${API_URL}/api/stream/toggle-live`, {
      method: 'POST',
      headers: { ...getAuthHeader() }
    });
    if (!response.ok) throw new Error('Failed to toggle live');
    return response.json();
  },

  cancelSession: async (message = '') => {
    const response = await fetch(`${API_URL}/api/stream/cancel-session`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify({ cancellation_message: message })
    });
    if (!response.ok) throw new Error('Failed to cancel session');
    return response.json();
  },

  uncancelSession: async () => {
    const response = await fetch(`${API_URL}/api/stream/uncancel-session`, {
      method: 'POST',
      headers: { ...getAuthHeader() }
    });
    if (!response.ok) throw new Error('Failed to restore session');
    return response.json();
  },

  // Weekly Schedule APIs
  getSchedule: async () => {
    const response = await fetch(`${API_URL}/api/stream/schedule`);
    if (!response.ok) throw new Error('Failed to fetch schedule');
    return response.json();
  },

  planCancellation: async (date, reason = '') => {
    const response = await fetch(`${API_URL}/api/stream/schedule/cancel`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify({ date, reason })
    });
    if (!response.ok) throw new Error('Failed to plan cancellation');
    return response.json();
  },

  restoreSession: async (date) => {
    const response = await fetch(`${API_URL}/api/stream/schedule/restore`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify({ date })
    });
    if (!response.ok) throw new Error('Failed to restore session');
    return response.json();
  },

  updateTradingDays: async (tradingDays) => {
    const response = await fetch(`${API_URL}/api/stream/schedule/trading-days`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify({ trading_days: tradingDays })
    });
    if (!response.ok) throw new Error('Failed to update trading days');
    return response.json();
  }
};

// Admin API
export const adminApi = {
  getUsers: async () => {
    const response = await fetch(`${API_URL}/api/admin/users`, {
      headers: { ...getAuthHeader() }
    });
    if (!response.ok) throw new Error('Failed to fetch users');
    return response.json();
  },

  getUsersWithSubscriptions: async () => {
    const response = await fetch(`${API_URL}/api/admin/users-with-subscriptions`, {
      headers: { ...getAuthHeader() }
    });
    if (!response.ok) throw new Error('Failed to fetch users with subscriptions');
    return response.json();
  },

  getUserSubscriptions: async (userId) => {
    const response = await fetch(`${API_URL}/api/admin/users/${userId}/subscriptions`, {
      headers: { ...getAuthHeader() }
    });
    if (!response.ok) throw new Error('Failed to fetch user subscriptions');
    return response.json();
  },

  assignMembership: async (userId, membershipId) => {
    const response = await fetch(`${API_URL}/api/admin/users/${userId}/assign-membership`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify({ membership_id: membershipId })
    });
    if (!response.ok) throw new Error('Failed to assign membership');
    return response.json();
  },

  revokeMembership: async (userId) => {
    const response = await fetch(`${API_URL}/api/admin/users/${userId}/revoke-membership`, {
      method: 'DELETE',
      headers: { ...getAuthHeader() }
    });
    if (!response.ok) throw new Error('Failed to revoke membership');
    return response.json();
  },

  assignCourse: async (userId, courseId) => {
    const response = await fetch(`${API_URL}/api/admin/users/${userId}/assign-course`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify({ course_id: courseId })
    });
    if (!response.ok) throw new Error('Failed to assign course');
    return response.json();
  },

  revokeCourseAccess: async (userId, courseId) => {
    const response = await fetch(`${API_URL}/api/admin/users/${userId}/revoke-course/${courseId}`, {
      method: 'DELETE',
      headers: { ...getAuthHeader() }
    });
    if (!response.ok) throw new Error('Failed to revoke course access');
    return response.json();
  },

  updateUser: async (userId, userData) => {
    const response = await fetch(`${API_URL}/api/admin/users/${userId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(userData)
    });
    if (!response.ok) throw new Error('Failed to update user');
    return response.json();
  },

  toggleUserStatus: async (userId) => {
    const response = await fetch(`${API_URL}/api/admin/users/${userId}/toggle-status`, {
      method: 'POST',
      headers: { ...getAuthHeader() }
    });
    if (!response.ok) throw new Error('Failed to toggle user status');
    return response.json();
  },
  
  deleteUserPermanently: async (userId) => {
    const response = await fetch(`${API_URL}/api/admin/users/${userId}`, {
      method: 'DELETE',
      headers: { ...getAuthHeader() }
    });
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.detail || 'Failed to delete user');
    }
    return data;
  },

  resetUserPassword: async (userEmail, newPassword) => {
    const response = await fetch(`${API_URL}/api/auth/admin/reset-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify({ user_email: userEmail, new_password: newPassword })
    });
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.detail || 'Failed to reset password');
    }
    return data;
  }
};

// Coupon API
export const couponApi = {
  // Admin functions
  getAll: async () => {
    const response = await fetch(`${API_URL}/api/coupons`, {
      headers: { ...getAuthHeader() }
    });
    if (!response.ok) throw new Error('Failed to fetch coupons');
    return response.json();
  },

  create: async (couponData) => {
    const response = await fetch(`${API_URL}/api/coupons`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(couponData)
    });
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.detail || 'Failed to create coupon');
    }
    return data;
  },

  update: async (couponId, couponData) => {
    const response = await fetch(`${API_URL}/api/coupons/${couponId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(couponData)
    });
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.detail || 'Failed to update coupon');
    }
    return data;
  },

  delete: async (couponId) => {
    const response = await fetch(`${API_URL}/api/coupons/${couponId}`, {
      method: 'DELETE',
      headers: { ...getAuthHeader() }
    });
    if (!response.ok) throw new Error('Failed to delete coupon');
    return response.json();
  },

  toggle: async (couponId) => {
    const response = await fetch(`${API_URL}/api/coupons/${couponId}/toggle`, {
      method: 'PATCH',
      headers: { ...getAuthHeader() }
    });
    if (!response.ok) throw new Error('Failed to toggle coupon status');
    return response.json();
  },

  // Public function - validate coupon
  validate: async (code, productId, productType, userId = null) => {
    const response = await fetch(`${API_URL}/api/coupons/validate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        code,
        product_id: productId,
        product_type: productType,
        user_id: userId
      })
    });
    if (!response.ok) throw new Error('Failed to validate coupon');
    return response.json();
  },

  // Called after successful purchase
  redeem: async (couponId, userId) => {
    const response = await fetch(`${API_URL}/api/coupons/${couponId}/redeem?user_id=${userId}`, {
      method: 'POST',
      headers: { ...getAuthHeader() }
    });
    if (!response.ok) throw new Error('Failed to redeem coupon');
    return response.json();
  }
};


// Quotes API
export const quotesApi = {
  getDaily: async () => {
    const response = await fetch(`${API_URL}/api/quotes/daily`);
    if (!response.ok) throw new Error('Failed to fetch daily quote');
    return response.json();
  },

  getRandom: async () => {
    const response = await fetch(`${API_URL}/api/quotes/random`);
    if (!response.ok) throw new Error('Failed to fetch random quote');
    return response.json();
  }
};


// Failed Payments API (Admin)
export const failedPaymentsApi = {
  getAll: async () => {
    const response = await fetch(`${API_URL}/api/membership/failed-payments`, {
      headers: { ...getAuthHeader() }
    });
    if (!response.ok) throw new Error('Failed to fetch failed payments');
    return response.json();
  },

  getCount: async () => {
    const response = await fetch(`${API_URL}/api/membership/failed-payments/count`, {
      headers: { ...getAuthHeader() }
    });
    if (!response.ok) throw new Error('Failed to fetch count');
    return response.json();
  },

  resolve: async (paymentId) => {
    const response = await fetch(`${API_URL}/api/membership/failed-payments/${paymentId}/resolve`, {
      method: 'PUT',
      headers: { ...getAuthHeader() }
    });
    if (!response.ok) throw new Error('Failed to resolve payment');
    return response.json();
  },

  addTest: async () => {
    const response = await fetch(`${API_URL}/api/membership/failed-payments/test`, {
      method: 'POST',
      headers: { ...getAuthHeader() }
    });
    if (!response.ok) throw new Error('Failed to add test payment');
    return response.json();
  },

  downgradeToFree: async (userId, opts = {}) => {
    const response = await fetch(`${API_URL}/api/membership/users/${userId}/downgrade-to-free`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify({
        reason: opts.reason || 'Manually downgraded from Admin Panel',
        failed_payment_id: opts.failedPaymentId || null,
      }),
    });
    if (!response.ok) throw new Error('Failed to downgrade user');
    return response.json();
  }
};


// New Members API (Admin)
export const newMembersApi = {
  getAll: async () => {
    const response = await fetch(`${API_URL}/api/membership/new-members`, {
      headers: { ...getAuthHeader() }
    });
    if (!response.ok) throw new Error('Failed to fetch new members');
    return response.json();
  },

  getCount: async () => {
    const response = await fetch(`${API_URL}/api/membership/new-members/count`, {
      headers: { ...getAuthHeader() }
    });
    if (!response.ok) throw new Error('Failed to fetch count');
    return response.json();
  },

  markProcessed: async (memberId) => {
    const response = await fetch(`${API_URL}/api/membership/new-members/${memberId}/processed`, {
      method: 'PUT',
      headers: { ...getAuthHeader() }
    });
    if (!response.ok) throw new Error('Failed to mark as processed');
    return response.json();
  },

  addTest: async () => {
    const response = await fetch(`${API_URL}/api/membership/new-members/test`, {
      method: 'POST',
      headers: { ...getAuthHeader() }
    });
    if (!response.ok) throw new Error('Failed to add test member');
    return response.json();
  },

  syncAlerts: async () => {
    const response = await fetch(`${API_URL}/api/admin/sync-new-member-alerts`, {
      method: 'POST',
      headers: { ...getAuthHeader() }
    });
    if (!response.ok) throw new Error('Failed to sync alerts');
    return response.json();
  },

  refreshData: async () => {
    const response = await fetch(`${API_URL}/api/admin/refresh-new-member-data`, {
      method: 'POST',
      headers: { ...getAuthHeader() }
    });
    if (!response.ok) throw new Error('Failed to refresh data');
    return response.json();
  },

  syncUserFromStripe: async (email) => {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', `${API_URL}/api/admin/sync-user-from-stripe/${encodeURIComponent(email)}`);
      
      // Set headers
      const token = localStorage.getItem('hyperdriveToken');
      if (token) {
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      }
      
      xhr.onload = function() {
        try {
          const data = JSON.parse(xhr.responseText);
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve(data);
          } else {
            reject(new Error(data.detail || data.message || 'Failed to sync from Stripe'));
          }
        } catch (e) {
          reject(new Error(xhr.responseText || 'Failed to sync from Stripe'));
        }
      };
      
      xhr.onerror = function() {
        reject(new Error('Network error while syncing from Stripe'));
      };
      
      xhr.send();
    });
  },

  processGuestPurchase: async (userId, sessionId, courseId = null) => {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', `${API_URL}/api/admin/users/${userId}/process-guest-purchase`);
      
      const token = localStorage.getItem('hyperdriveToken');
      if (token) {
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      }
      xhr.setRequestHeader('Content-Type', 'application/json');
      
      xhr.onload = function() {
        try {
          const data = JSON.parse(xhr.responseText);
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve(data);
          } else {
            reject(new Error(data.detail || data.message || 'Failed to process guest purchase'));
          }
        } catch (e) {
          reject(new Error(xhr.responseText || 'Failed to process guest purchase'));
        }
      };
      
      xhr.onerror = function() {
        reject(new Error('Network error while processing guest purchase'));
      };
      
      const body = { session_id: sessionId };
      if (courseId) body.course_id = courseId;
      xhr.send(JSON.stringify(body));
    });
  },

  createStripeSubscription: async (userId) => {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', `${API_URL}/api/admin/users/${userId}/create-stripe-subscription`);
      
      const token = localStorage.getItem('hyperdriveToken');
      if (token) {
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      }
      
      xhr.onload = function() {
        try {
          const data = JSON.parse(xhr.responseText);
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve(data);
          } else {
            reject(new Error(data.detail || data.message || 'Failed to create subscription'));
          }
        } catch (e) {
          reject(new Error(xhr.responseText || 'Failed to create subscription'));
        }
      };
      
      xhr.onerror = function() {
        reject(new Error('Network error while creating subscription'));
      };
      
      xhr.send();
    });
  }
};


// Analytics API (Admin Dashboard)
export const analyticsApi = {
  getOverview: async (days = 30) => {
    const response = await fetch(`${API_URL}/api/analytics/overview?days=${days}`, {
      headers: { ...getAuthHeader() }
    });
    if (!response.ok) throw new Error('Failed to fetch dashboard overview');
    return response.json();
  },

  getRevenueChart: async (days = 30) => {
    const response = await fetch(`${API_URL}/api/analytics/revenue-chart?days=${days}`, {
      headers: { ...getAuthHeader() }
    });
    if (!response.ok) throw new Error('Failed to fetch revenue chart data');
    return response.json();
  },

  getRecentTransactions: async (limit = 10) => {
    const response = await fetch(`${API_URL}/api/analytics/recent-transactions?limit=${limit}`, {
      headers: { ...getAuthHeader() }
    });
    if (!response.ok) throw new Error('Failed to fetch recent transactions');
    return response.json();
  },

  getMemberStats: async () => {
    const response = await fetch(`${API_URL}/api/analytics/member-stats`, {
      headers: { ...getAuthHeader() }
    });
    if (!response.ok) throw new Error('Failed to fetch member stats');
    return response.json();
  },

  getAlerts: async () => {
    const response = await fetch(`${API_URL}/api/analytics/alerts`, {
      headers: { ...getAuthHeader() }
    });
    if (!response.ok) throw new Error('Failed to fetch alerts');
    return response.json();
  },

  getExpiringMemberships: async () => {
    const response = await fetch(`${API_URL}/api/analytics/expiring-memberships`, {
      headers: { ...getAuthHeader() }
    });
    if (!response.ok) throw new Error('Failed to fetch expiring memberships');
    return response.json();
  },

  getRecentActivity: async (limit = 20) => {
    const response = await fetch(`${API_URL}/api/analytics/recent-activity?limit=${limit}`, {
      headers: { ...getAuthHeader() }
    });
    if (!response.ok) throw new Error('Failed to fetch recent activity');
    return response.json();
  }
};

// Telegram API (Admin)
export const telegramApi = {
  getConfig: async () => {
    const response = await fetch(`${API_URL}/api/telegram/config`, {
      headers: { ...getAuthHeader() }
    });
    if (!response.ok) throw new Error('Failed to fetch Telegram config');
    return response.json();
  },

  updateConfig: async (chatId, topicId = null, botToken = null) => {
    const response = await fetch(`${API_URL}/api/telegram/config`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify({ chat_id: chatId, topic_id: topicId, bot_token: botToken })
    });
    if (!response.ok) throw new Error('Failed to update Telegram config');
    return response.json();
  },

  testConnection: async () => {
    const response = await fetch(`${API_URL}/api/telegram/test`, {
      method: 'POST',
      headers: { ...getAuthHeader() }
    });
    if (!response.ok) throw new Error('Failed to test Telegram connection');
    return response.json();
  },

  sendMessage: async (message) => {
    const response = await fetch(`${API_URL}/api/telegram/send`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify({ message })
    });
    if (!response.ok) throw new Error('Failed to send Telegram message');
    return response.json();
  },

  getHelp: async () => {
    const response = await fetch(`${API_URL}/api/telegram/help`, {
      headers: { ...getAuthHeader() }
    });
    if (!response.ok) throw new Error('Failed to fetch setup help');
    return response.json();
  }
};

// Coaching API
export const coachingApi = {
  // Coach alerts (admin-only)
  listAlerts: async () => {
    const response = await fetch(`${API_URL}/api/coaching/alerts`, {
      headers: { ...getAuthHeader() }
    });
    if (!response.ok) throw new Error('Failed to fetch coach alerts');
    return response.json();
  },
  resolveAlert: async (alertId) => {
    const response = await fetch(`${API_URL}/api/coaching/alerts/${alertId}/resolve`, {
      method: 'POST',
      headers: { ...getAuthHeader() }
    });
    if (!response.ok) throw new Error('Failed to resolve alert');
    return response.json();
  },
  runAlertScan: async () => {
    const response = await fetch(`${API_URL}/api/coaching/alerts/run-scan`, {
      method: 'POST',
      headers: { ...getAuthHeader() }
    });
    if (!response.ok) throw new Error('Failed to run scan');
    return response.json();
  },

  // Student endpoints
  getMyStatus: async () => {
    const response = await fetch(`${API_URL}/api/coaching/my-status`, {
      headers: { ...getAuthHeader() }
    });
    if (!response.ok) throw new Error('Failed to fetch coaching status');
    return response.json();
  },

  toggleSharing: async (enabled) => {
    const response = await fetch(`${API_URL}/api/coaching/toggle-sharing`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify({ enabled })
    });
    if (!response.ok) throw new Error('Failed to toggle sharing');
    return response.json();
  },

  getMyMessages: async () => {
    const response = await fetch(`${API_URL}/api/coaching/messages`, {
      headers: { ...getAuthHeader() }
    });
    if (!response.ok) throw new Error('Failed to fetch messages');
    return response.json();
  },

  sendMessageToCoach: async (message) => {
    const response = await fetch(`${API_URL}/api/coaching/messages`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify({ message })
    });
    if (!response.ok) throw new Error('Failed to send message');
    return response.json();
  },

  getMySessions: async () => {
    const response = await fetch(`${API_URL}/api/coaching/my-sessions`, {
      headers: { ...getAuthHeader() }
    });
    if (!response.ok) throw new Error('Failed to fetch sessions');
    return response.json();
  },

  // Admin endpoints
  getStudents: async () => {
    const response = await fetch(`${API_URL}/api/coaching/students`, {
      headers: { ...getAuthHeader() }
    });
    if (!response.ok) throw new Error('Failed to fetch students');
    return response.json();
  },

  getStudentDetail: async (studentId, month = null) => {
    // Add timeout to prevent hanging requests - increased to 60s for large datasets
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 60000); // 60 second timeout
    
    try {
      const url = month 
        ? `${API_URL}/api/coaching/students/${studentId}?month=${month}`
        : `${API_URL}/api/coaching/students/${studentId}`;
      const response = await fetch(url, {
        headers: { ...getAuthHeader() },
        signal: controller.signal
      });
      clearTimeout(timeoutId);
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to fetch student detail');
      }
      return response.json();
    } catch (error) {
      clearTimeout(timeoutId);
      if (error.name === 'AbortError') {
        throw new Error('Request timed out - please try again or select a specific month');
      }
      throw error;
    }
  },

  sendMessageToStudent: async (studentId, message) => {
    const response = await fetch(`${API_URL}/api/coaching/students/${studentId}/message`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify({ message })
    });
    if (!response.ok) throw new Error('Failed to send message');
    return response.json();
  },

  getStudentCalendar: async (studentId, month = null) => {
    const url = month 
      ? `${API_URL}/api/coaching/students/${studentId}/calendar?month=${month}`
      : `${API_URL}/api/coaching/students/${studentId}/calendar`;
    const response = await fetch(url, {
      headers: { ...getAuthHeader() }
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error('Calendar API error:', response.status, errorData);
      throw new Error(errorData.detail || 'Failed to fetch calendar');
    }
    return response.json();
  },

  // Mentorship endpoints
  getMentorshipStudents: async () => {
    const response = await fetch(`${API_URL}/api/coaching/mentorship/students`, {
      headers: { ...getAuthHeader() }
    });
    if (!response.ok) throw new Error('Failed to fetch mentorship students');
    return response.json();
  },

  addMentorshipStudent: async (userId) => {
    const response = await fetch(`${API_URL}/api/coaching/mentorship/add`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify({ user_id: userId })
    });
    if (!response.ok) throw new Error('Failed to add mentorship student');
    return response.json();
  },

  removeMentorshipStudent: async (userId) => {
    const response = await fetch(`${API_URL}/api/coaching/mentorship/remove`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify({ user_id: userId })
    });
    if (!response.ok) throw new Error('Failed to remove mentorship student');
    return response.json();
  },

  getStudentSchedule: async (studentId) => {
    const response = await fetch(`${API_URL}/api/coaching/mentorship/students/${studentId}/schedule`, {
      headers: { ...getAuthHeader() }
    });
    if (!response.ok) throw new Error('Failed to fetch schedule');
    return response.json();
  },

  setStudentSchedule: async (studentId, scheduleData) => {
    const response = await fetch(`${API_URL}/api/coaching/mentorship/students/${studentId}/schedule`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(scheduleData)
    });
    if (!response.ok) throw new Error('Failed to set schedule');
    return response.json();
  },

  deleteStudentSchedule: async (studentId) => {
    const response = await fetch(`${API_URL}/api/coaching/mentorship/students/${studentId}/schedule`, {
      method: 'DELETE',
      headers: { ...getAuthHeader() }
    });
    if (!response.ok) throw new Error('Failed to delete schedule');
    return response.json();
  },

  getStudentSessions: async (studentId) => {
    const response = await fetch(`${API_URL}/api/coaching/mentorship/students/${studentId}/sessions`, {
      headers: { ...getAuthHeader() }
    });
    if (!response.ok) throw new Error('Failed to fetch sessions');
    return response.json();
  },

  createSession: async (studentId, sessionData) => {
    const response = await fetch(`${API_URL}/api/coaching/mentorship/students/${studentId}/sessions`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(sessionData)
    });
    if (!response.ok) throw new Error('Failed to create session');
    return response.json();
  },

  updateSession: async (sessionId, sessionData) => {
    const response = await fetch(`${API_URL}/api/coaching/mentorship/sessions/${sessionId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(sessionData)
    });
    if (!response.ok) throw new Error('Failed to update session');
    return response.json();
  },

  deleteSession: async (sessionId) => {
    const response = await fetch(`${API_URL}/api/coaching/mentorship/sessions/${sessionId}`, {
      method: 'DELETE',
      headers: { ...getAuthHeader() }
    });
    if (!response.ok) throw new Error('Failed to delete session');
    return response.json();
  },

  uploadSessionVideo: async (sessionId, file, onProgress) => {
    return new Promise((resolve, reject) => {
      const formData = new FormData();
      formData.append('file', file);

      const xhr = new XMLHttpRequest();
      xhr.open('POST', `${API_URL}/api/coaching/mentorship/sessions/${sessionId}/upload-video`);
      
      const token = localStorage.getItem('hyperdriveToken');
      if (token) {
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      }

      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable && onProgress) {
          onProgress(Math.round((e.loaded / e.total) * 100));
        }
      };

      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve(JSON.parse(xhr.responseText));
        } else {
          reject(new Error('Upload failed'));
        }
      };

      xhr.onerror = () => reject(new Error('Upload failed'));
      xhr.send(formData);
    });
  },

  // Admin notification config
  getAdminNotificationConfig: async () => {
    const response = await fetch(`${API_URL}/api/coaching/admin/notification-config`, {
      headers: { ...getAuthHeader() }
    });
    if (!response.ok) throw new Error('Failed to fetch notification config');
    return response.json();
  },

  updateAdminNotificationConfig: async (config) => {
    const response = await fetch(`${API_URL}/api/coaching/admin/notification-config`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(config)
    });
    if (!response.ok) throw new Error('Failed to update notification config');
    return response.json();
  },

  testAdminNotification: async () => {
    const response = await fetch(`${API_URL}/api/coaching/admin/test-notification`, {
      method: 'POST',
      headers: { ...getAuthHeader() }
    });
    if (!response.ok) throw new Error('Failed to send test notification');
    return response.json();
  }
};
